import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import Span from "./span";
import {ProgressBar} from "primereact/progressbar";
import {Checkbox} from "primereact/checkbox";
import {useSelector} from "react-redux";
import {useTranslate} from "../useTranslate";
import ReactAudioPlayer from "react-audio-player";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import LimitByDate from "./limit-by-date";
import {Paginator} from "primereact/paginator";
import DatatableEmptyMessage from "./datatable-empty-message";
import {Chip} from "primereact/chip";
import AvatarGroup from "./avatar-group";
import Avatar from "./avatar";

function CellViewType({type, value, format, valueClassName, cellClassName, valueIcon, cellIcon, masked}) {

    switch (type) {
        case 'TEXT':
        case 'NUMBER':
        case 'PERSIAN_DATE':
        case 'GREGORIAN_DATE':
        case 'TIME_SINCE':
        case 'TIME_DURATION':
            return (
                <div className={`flex gap-1 w-fit align-items-center ${cellClassName?cellClassName:valueClassName}`}>
                    {(cellIcon||valueIcon)&&<span className={cellIcon?cellIcon:valueIcon}/>}
                    <Span spanClassName={`ltr ${masked&&"masked masked-on"}`} type={type} format={format}>{!masked&&value}</Span>
                </div>)
        case 'PROGRESS_BAR':
            return <ProgressBar value={value}/>
        case 'CHECK_BOX':
            return <Checkbox checked={value}/>
        case 'AUDIO':
            return value&&<ReactAudioPlayer src={value} controls/>
        case 'IMAGE':
            return <img src={value} className="w-3 border-round"/>
        case "CHIP":
            return value&&<div className="flex flex-wrap gap-1">{value.split(";").map(e=><Chip className="text-xs" label={e}/>)}</div>
        case "AVATAR_GROUP":
            return value&&<AvatarGroup>{Array.isArray(value)
                ? value.map((membership) => <Avatar memberData={membership?.member}/>)
                : <Avatar memberData={value?.member}/>}</AvatarGroup>
        default:return  (
            <div className={`flex gap-1 w-fit align-items-center ${cellClassName?cellClassName:valueClassName}`}>
                {(cellIcon||valueIcon)&&<span className={cellIcon?cellIcon:valueIcon}/>}
                <Span spanClassName={`ltr ${masked&&"masked masked-on"}`} type={type} format={format}>{!masked&&value}</Span>
            </div>)
    }
}

function Filter({setVisible, visible, fieldsConfig, onApplied}) {
    const translate = useTranslate();
    const {config} = useSelector((state) => state.app);
    const [filters, setFilters] = useState({})
    return(
        <Dialog footer={<div><Button className="p-button-sm" onClick={()=>{onApplied && onApplied({filters})}} label={translate("apply")}/></div>} className={`${config.dir}`} header={translate("filter")} onHide={()=>{setVisible(false)}} visible={visible}>
            <div className="flex flex-column gap-2">
                {Object.keys(fieldsConfig).map(fc=>{
                    if (fieldsConfig[fc].filter){
                        return (
                            <div className="flex gap-2 justify-content-between align-items-center">
                                <span>{translate(fc)}</span>
                                <div className="flex gap-2 align-items-center">
                                    {filters[fc]&&<span className="pi pi-trash text-red-500 cursor-pointer" onClick={()=>{setFilters(prvValue=>{let newValue = {...prvValue}; delete newValue[fc]; return newValue}) }} />}
                                    <FilterInputType fieldConfig={fieldsConfig[fc]} value={filters[fc]} onChange={(e)=>{setFilters({...filters, [fc]:e.value})}}/>
                                </div>
                            </div>
                        )
                    }
                })}
            </div>
        </Dialog>
    )
}

function Sorting({setVisible, visible, fieldsConfig, onApplied}) {
    const translate = useTranslate();
    const {config} = useSelector((state) => state.app);
    const [sorting, setSorting] = useState({})
    return(
        <Dialog footer={<div><Button className="p-button-sm" onClick={()=>{onApplied && onApplied({sorting})}} label={translate("apply")}/></div>} className={`${config.dir} w-3`} header={translate("sorting")} onHide={()=>{setVisible(false)}} visible={visible}>
            <div className="flex flex-column gap-2">
                {Object.keys(fieldsConfig).map(fc=>{
                    if (fieldsConfig[fc].sorting){
                        return (
                            <div className="flex gap-2 justify-content-between align-items-center">
                                <span>{translate(fc)}</span>
                                <div className="flex gap-2 align-items-center">
                                    {sorting[fc]&&<span className="pi pi-trash text-red-500 cursor-pointer" onClick={()=>{setSorting(prvValue=>{let newValue = {...prvValue}; delete newValue[fc]; return newValue}) }} />}
                                    <Dropdown options={[{label:translate("asc"), value:'asc'},{label:translate("desc"), value:'desc'}]} value={sorting[fc]} onChange={(e)=>{setSorting({...sorting, [fc]:e.value})}}/>
                                    {/*<FilterInputType fieldConfig={fieldsConfig[fc]} value={sorting[fc]} onChange={(e)=>{setSorting({...sorting, [fc]:e.value})}}/>*/}
                                </div>
                            </div>
                        )
                    }
                })}
            </div>
        </Dialog>
    )
}

function FilterInputType({fieldConfig, value, onChange}) {
    const translate = useTranslate();

    if (fieldConfig['filter-options']&&fieldConfig['filter-options'].length>1){
        return <Dropdown options={fieldConfig['filter-options'].map(e=>{return {label:translate(e), value:e}})} value={value} onChange={onChange}/>
    }
    switch (fieldConfig.type) {
        case "TEXT":return <InputText value={value} onChange={(e)=>{onChange({value:e.target.value})}}/>
        case "PERSIAN_DATE": return <LimitByDate selectedLimit={value&&value.value} onChange={(e)=>{onChange({value:e})}}/>
    }
}

function Table({value = [], fields, fieldsConfig, showIndex=false, cellClassName, showPagination=true, header, showHeader=true, footer, showFooter=true, onRowDelete, onRowClick, totalRows, rowsPerPage=10, currentPage=1, setCurrentPage, setFilters, setSorting, dateLimit, setDateLimit, loading, scrollDirection="both", selectionMode, showSelectAll, rowGroupMode,rowGroupHeaderTemplate, rowGroupFooterTemplate, groupRowsBy, expandedRows, dataKey, selection, rowClassName, onSelectionChange, onRowSelect, style={height:'calc(100vh - 7rem)'}}) {

    const {config, isDesktop} = useSelector((state) => state.app);
    const [filterVisible, setFilterVisible] = useState(false);
    const [sortingVisible, setSortingVisible] = useState(false);

    const translate = useTranslate();

    if (!fields && value.length>0){
        fields = Object.keys(value[0]);
    }

    const isFilterable = (fieldsConfig={})=>{
        return Object.values(fieldsConfig).some(fc => fc.filter === true);
    }

    const fitFilter = (prvFilter, fieldsConfig) => {
        const newFilter = {};

        for (const key in prvFilter) {
            if (!prvFilter.hasOwnProperty(key)) continue;

            // Get the mapped SQL field name, or fall back to original key
            const fieldConfig = fieldsConfig[key];
            const queryField = fieldConfig?.['field-name-in-query'] || key;
            newFilter[queryField] = prvFilter[key];

        }
        return newFilter;
    };

    const fitSorting = (prvSorting, fieldsConfig) => {
        const newSorting = {};

        for (const key in prvSorting) {
            if (!prvSorting.hasOwnProperty(key)) continue;

            // Get the mapped SQL field name, or fall back to original key
            const fieldConfig = fieldsConfig[key];
            const queryField = fieldConfig?.['field-name-in-query'] || key;
            newSorting[queryField] = prvSorting[key];

        }
        return newSorting;
    };
    // console.log(selection, value)

    return (
        <div className={`w-full h-full flex flex-column ${loading&& "p-disabled"}`}>
            <DataTable  dataKey={dataKey} expandedRows={expandedRows} groupRowsBy={groupRowsBy} rowGroupMode={rowGroupMode} rowGroupHeaderTemplate={rowGroupHeaderTemplate} rowGroupFooterTemplate={rowGroupFooterTemplate} showSelectAll={showSelectAll} selectionMode={selectionMode} selection={selection} rowHover={onRowClick} onRowClick={(e)=>{onRowClick&&onRowClick({value:e.data})}} onSelectionChange={onSelectionChange} onRowSelect={onRowSelect} rowClassName={rowClassName??(onRowClick&&"cursor-pointer")} emptyMessage={<DatatableEmptyMessage loading={loading}/>} header={header?header:showHeader&&<div className="flex justify-content-between align-items-center"><div className="flex gap-2">{dateLimit&&<LimitByDate selectedLimit={dateLimit.value} onChange={(e)=>{setDateLimit(e)}}/>}<Button className="flex p-button-outlined p-button-sm gap-1 text-sm" visible={isFilterable(fieldsConfig)} onClick={()=>{setFilterVisible(true)}}><span className="pi pi-filter text-xs"/><span>{translate("filter")}</span></Button></div>{totalRows&&<span className="text-xs font-normal">{`${translate("total-records")}: ${totalRows}`}</span>}</div>} footer={footer?footer:showFooter&&<div>{showPagination&&<Paginator className="bg-transparent" first={rowsPerPage*currentPage} onPageChange={(e)=>{setCurrentPage(e.page);}} dir={"ltr"} totalRecords={totalRows} rows={rowsPerPage}  />}</div>} tableClassName="w-full text-sm" cellClassName={`p-1 ${cellClassName}`} className={`w-full${!isDesktop&&"overflow-auto"}`} scrollable={isDesktop} scrollDirection={scrollDirection} style={style} scrollHeight="flex" responsiveLayout="stack" value={value} >
                {selectionMode&&<Column className={`${config.dir==='rtl'?"text-right":"text-left"} pr-3 pl-3`} body={(item)=>(<Checkbox onChange={(e)=>{onRowSelect({checked:e.checked, data:item})}} checked={selection.findIndex(e=>e.id===item.id)>=0}/>)}/>}
                {showIndex && <Column className={`${config.dir==='rtl'?"text-right":"text-left"} ${fieldsConfig["index"]&&fieldsConfig["index"]['class-name']}`} headerClassName="text-xs p-1"  header={translate('index')} body={(item)=>(<span>{(value.findIndex(e=>e===item)+1)+currentPage*rowsPerPage}</span>)}/>}
                {fields&&fields.map((f)=>{
                    if (fieldsConfig && fieldsConfig[f]){

                        return fieldsConfig[f].type === "ACTIONS" ? (
                            <Column header={<div className="flex gap-2 align-items-center"><span className={fieldsConfig[f]['class-name']}>{translate(fieldsConfig[f]['alias'] ? fieldsConfig[f]['alias'] : f)}</span></div>} headerClassName="text-xs p-1" bodyClassName={fieldsConfig[f]['class-name']} body={(item) => fieldsConfig[f].value(item)}/>)
                            : (<Column sortable={fieldsConfig[f]['local-sorting']} className={`${config.dir === 'rtl' ? "text-right" : "text-left"} ${fieldsConfig[f]['class-name']}`} headerClassName="text-xs p-1" bodyClassName={fieldsConfig[f]['class-name']} header={fieldsConfig[f]['local-sorting'] ? translate(f) : <div className="flex gap-2 align-items-center">{fieldsConfig[f]['filter'] && <span className="pi pi-search cursor-pointer text-xs" onClick={() => {setFilterVisible(true)}}/>}<span className={fieldsConfig[f]['class-name']}>{translate(fieldsConfig[f]['alias'] ? fieldsConfig[f]['alias'] : f)}</span>{fieldsConfig[f]['sorting'] && <span className="pi pi-sort cursor-pointer text-xs" onClick={() => {setSortingVisible(true)}}/>}</div>} field={f}
                                    body={(item) => {
                                        const _value = fieldsConfig[f]['value-exchange']?fieldsConfig[f]['value-exchange'][item[f]]:item[f];
                                        return (fieldsConfig[f]['more-details'] ?
                                            <div className="flex align-items-center gap-2 justify-content-between">
                                                <div className="flex align-items-center gap-2">
                                                    {fieldsConfig[f]['cell-icon'] && <Avatar shape="circle" size="large" className="surface-100" icon={fieldsConfig[f]['cell-icon']}/>}
                                                    <div className="flex flex-column">
                                                        <CellViewType masked={fieldsConfig[f]['masked']&&fieldsConfig[f]['masked'](item)} valueIcon={fieldsConfig[f]['value-icon'] && fieldsConfig[f]['value-icon'][item[f]]} cellClassName={fieldsConfig[f]['cell-class-name']} valueClassName={fieldsConfig[f]['value-class-name'] && fieldsConfig[f]['value-class-name'][item[f]]} type={fieldsConfig[f]['type']} format={fieldsConfig[f]['format']} value={fieldsConfig[f]['translate'] ? translate(_value) : _value}/>
                                                        {fieldsConfig[f]['more-details'].map(md => {
                                                            const _md_value = md['value-exchange']?md['value-exchange'][item[md['field']]]:item[md['field']];
                                                            return(
                                                                item[md['field']] && <CellViewType masked={fieldsConfig[f]['masked']&&fieldsConfig[f]['masked'](item)} valueIcon={md['value-icon'] && md['value-icon'][item[md['field']]]} cellIcon={md['cell-icon']} cellClassName={md['cell-class-name']} valueClassName={md['value-class-name'] && md['value-class-name'][item[md['field']]]} type={md['type']} format={md['format']} value={md['translate'] ? translate(_md_value) : _md_value}/>
                                                            )}
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    {fieldsConfig[f].actions&&fieldsConfig[f].actions(item)}
                                                </div>
                                            </div> :
                                            <div className="flex align-items-center gap-2 justify-content-between">
                                                <div className="flex align-items-center gap-2">
                                                    {fieldsConfig[f]['cell-icon'] && <Avatar shape="circle" size="large" icon={fieldsConfig[f]['cell-icon']}/>}
                                                    <CellViewType masked={fieldsConfig[f]['masked']&&fieldsConfig[f]['masked'](item)} valueIcon={fieldsConfig[f]['value-icon'] && fieldsConfig[f]['value-icon'][item[f]]} cellClassName={fieldsConfig[f]['cell-class-name']} valueClassName={fieldsConfig[f]['value-class-name'] && fieldsConfig[f]['value-class-name'][item[f]]} type={fieldsConfig[f]['type']} format={fieldsConfig[f]['format']} value={fieldsConfig[f]['translate'] ? translate(_value) : _value}/>
                                                </div>
                                                <div>
                                                    {fieldsConfig[f].actions&&fieldsConfig[f].actions(item)}
                                                </div>
                                            </div>)
                                    }}/>)
                    }else {
                        return <Column className={`${config.dir==='rtl'?"text-right":"text-left"} w-10rem`} header={translate(f)} headerClassName="text-xs p-1" field={f}/>
                    }
                })}
                {(onRowDelete)&&<Column  className={`${config.dir==='rtl'?"text-left":"text-right"}`} body={(item)=>(
                    <div className="flex align-items-center gap-2 w-fit">
                        {onRowDelete && <span className="pi pi-trash text-red-500 cursor-pointer" onClick={()=>{onRowDelete(item)}}/>}
                        {/*{onRowClick && <span className="pi pi-eye text-blue-500 cursor-pointer" onClick={()=>{onRowClick(item)}}/>}*/}
                    </div>)}/>}
            </DataTable>
            {fieldsConfig&& <Filter visible={filterVisible} setVisible={setFilterVisible} fieldsConfig={fieldsConfig} onApplied={(e)=>{setFilterVisible(false); setFilters&&setFilters( fitFilter(e.filters, fieldsConfig) )}}/>}
            {fieldsConfig&& <Sorting visible={sortingVisible} setVisible={setSortingVisible} fieldsConfig={fieldsConfig} onApplied={(e)=>{setSortingVisible(false); setSorting&&setSorting( fitSorting(e.sorting, fieldsConfig) )}}/>}
        </div>
    );
}

//demo
/**
 *
 * const [value, setValue] = useState([{is_married:false, name: 'abbas', family:'mazravi', birthdate:new DateObject().toUnix(), purchased_value:10000000, level: 50},{is_married:true, name: 'sahar', family:'mazravi', birthdate:new DateObject().toUnix(), purchased_value:20000000, level:90}]);
 *     const base = useBase();
 *
 *     return (
 *         <div className="p-4">
 *             {
 *                 base.state.configs && (
 *                     <div className={`${base.state.configs.init.DIR}`}>
 *                         <Table arr={value} showIndex={true} onRowDelete={(e)=>{console.log(e)}} onRowClick={(e)=>{console.log(e)}} fieldsType={{birthdate:'PERSIAN_DATE', purchased_value:"NUMBER", level:"PROGRESS_BAR", is_married:"CHECK_BOX"}}/>
 *                     </div>
 *                 )
 *             }
 *         </div>
 *     );
 */

export default Table;
